import { Avatar, Badge, Button, Menu, MenuItem, MenuLabel, MenuSeparator, cn } from "@payezy/ui";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Activity, ArrowLeftRight, Building2, ChevronsUpDown, FileCheck2, Home, LogOut, Plus, Settings, Shield, Users, Wallet } from "lucide-react";
import { Suspense, type ReactNode } from "react";
import { kratos, viewAs } from "../lib/api";
import { orgStatusLabel, useMe, useOrg } from "../lib/org";
import { Brand } from "./Brand";

type NavItem = { to: string; label: string; icon: ReactNode; perm?: string; exact?: boolean };

const nav: NavItem[] = [
  { to: "/o/$orgId", label: "Overview", icon: <Home className="size-4" />, perm: "accounts:view", exact: true },
  { to: "/o/$orgId/payments", label: "Payments", icon: <ArrowLeftRight className="size-4" />, perm: "payments:view" },
  { to: "/o/$orgId/recipients", label: "Recipients", icon: <Wallet className="size-4" />, perm: "counterparties:view" },
  { to: "/o/$orgId/verification", label: "Verification", icon: <FileCheck2 className="size-4" />, perm: "onboarding:manage" },
  { to: "/o/$orgId/team", label: "Team", icon: <Users className="size-4" />, perm: "members:view" },
  { to: "/o/$orgId/activity", label: "Activity", icon: <Activity className="size-4" />, perm: "activity:view" },
  { to: "/o/$orgId/settings", label: "Settings", icon: <Settings className="size-4" /> },
];

export function AppShell() {
  const me = useMe();
  const org = useOrg();
  const navigate = useNavigate();
  const status = orgStatusLabel[org.org_status];
  const imp = me.session.impersonation;
  const visible = nav.filter((n) => !n.perm || org.permissions.includes(n.perm));
  const fullName = `${me.user.first_name} ${me.user.last_name}`.trim();

  return (
    <div className="min-h-dvh">
      {imp && (
        <div className="sticky top-0 z-30 flex items-center justify-center gap-3 bg-amber-400 px-4 py-1.5 text-sm font-medium text-amber-950">
          <Shield className="size-4" />
          Payezy staff view-as session ({imp.scope === "read" ? "read-only" : "read & write"}) for {fullName}
          <Button size="sm" variant="secondary" onClick={() => { viewAs.clear(); window.location.assign("/view-as/ended"); }}>
            End session
          </Button>
        </div>
      )}
      <div className="flex">
        <aside className="sticky top-0 flex h-dvh w-60 shrink-0 flex-col border-r border-ink-200 bg-white px-3 py-4">
          <div className="px-2 pb-4"><Brand /></div>
          <Menu
            align="start"
            trigger={
              <button className="mb-4 flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left ring-1 ring-ink-200 hover:bg-ink-50">
                <Building2 className="size-4 text-ink-500" />
                <span className="flex-1 truncate text-sm font-medium">{org.org_name}</span>
                <ChevronsUpDown className="size-4 text-ink-400" />
              </button>
            }
          >
            <MenuLabel>Accounts</MenuLabel>
            {me.memberships.map((m) => (
              <MenuItem key={m.org_id} onSelect={() => navigate({ to: "/o/$orgId", params: { orgId: m.org_id } })}>
                <span className="flex-1 truncate">{m.org_name}</span>
                <span className="text-xs text-ink-400">{m.org_type === "business" ? "Business" : "Personal"}</span>
              </MenuItem>
            ))}
            {!imp && (
              <>
                <MenuSeparator />
                <MenuItem onSelect={() => navigate({ to: "/welcome" })}>
                  <Plus className="size-4" /> Open another account
                </MenuItem>
              </>
            )}
          </Menu>
          <nav className="flex flex-1 flex-col gap-0.5">
            {visible.map((n) => (
              <Link
                key={n.to}
                to={n.to as "/o/$orgId"}
                params={{ orgId: org.org_id }}
                activeOptions={{ exact: n.exact }}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                activeProps={{ className: "bg-ink-100 text-ink-950" }}
              >
                {n.icon}
                {n.label}
              </Link>
            ))}
          </nav>
          <Menu
            align="start"
            trigger={
              <button className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-ink-50">
                <Avatar name={fullName || me.user.email} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{fullName || me.user.email}</span>
                  <span className="block truncate text-xs text-ink-500">{org.role_name}</span>
                </span>
              </button>
            }
          >
            <MenuLabel>{me.user.email}</MenuLabel>
            <MenuItem onSelect={() => navigate({ to: "/settings/security" })}>
              <Shield className="size-4" /> Security
            </MenuItem>
            {!imp && (
              <MenuItem onSelect={() => void kratos.logout(window.location.origin + "/auth/login")}>
                <LogOut className="size-4" /> Sign out
              </MenuItem>
            )}
          </Menu>
        </aside>
        <main className="min-w-0 flex-1">
          {status && org.org_status !== "active" && (
            <div className={cn("border-b px-8 py-2.5 text-sm", org.org_status === "kyc_pending" ? "border-brand-100 bg-brand-50 text-brand-700" : "border-amber-200 bg-amber-50 text-amber-900")}>
              <Badge tone={status.tone} className="mr-2">{status.label}</Badge>
              {org.org_status === "kyc_required" && (
                <>
                  Verify your {org.org_type === "business" ? "business" : "identity"} to open your account.{" "}
                  {org.permissions.includes("onboarding:manage") && (
                    <Link to={"/o/$orgId/verification" as "/o/$orgId"} params={{ orgId: org.org_id }} className="font-medium underline">Start verification</Link>
                  )}
                </>
              )}
              {org.org_status === "kyc_pending" && "We're reviewing your information. This usually takes a few minutes."}
              {org.org_status === "restricted" && "Outgoing payments are temporarily restricted. Contact support for details."}
              {org.org_status === "suspended" && "This account is suspended. Contact support@payezy.io."}
            </div>
          )}
          <div className="mx-auto max-w-6xl px-8 py-8">
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
